/**
 * RBAC policy wiring.
 *
 * The dashboard runs the minimal-token model: the JWT carries role NAMES only,
 * and the `SessionManager` resolves effective scopes/`role_permissions` against
 * the Terraform-published policy at {@link policySource}, mirroring them back
 * onto `session.claims.role_permissions`. The render-time `<PermissionProvider>`
 * then gates UI from that map via the zero-cost JWT fast-path (no casbin bundle).
 *
 * {@link buildTimeFallbackPermissions} covers the air-gapped / offline case: a
 * policy CSV baked into the bundle (`VITE_CASBIN_POLICY`, or the legacy
 * `REACT_APP_CASBIN_PERMISSION_FROM_SSM`).
 */
import { buildTimePermissionSet, type PermissionSet, type PolicySource } from "@authe/fe-commons/auth";
import { appEnv, envSource } from "./env";

/**
 * Where the SessionManager fetches the role→scope policy. fe-commons resolves
 * everything from the deploy env: `policyCdnUrl` defaults to the CORS-enabled
 * CloudFront host (`https://cdn.{domain}`), and {@link resolvePolicyUrl} builds
 * the canonical published path
 * `/public/GET/cdn/{env}/global/permissions-data/role_permissions.json`
 * (auth0-mgmt `cdn-publish.tf`), mapping the local deploy env to the published
 * `sandbox` objects — so no per-app CDN/env wiring is needed.
 */
export const policySource: PolicySource = {
  baseUrl: appEnv.policyCdnUrl,
  env: appEnv.env,
};

/** Synchronous fallback PermissionSet from a baked policy CSV + role names. */
export function buildTimeFallbackPermissions(roles: readonly string[] | null): PermissionSet {
  return buildTimePermissionSet({ roles, source: envSource });
}
